import { useEffect, useRef, useState } from 'react';
import {
  AMBIENT_AUCTION_WHEEL_FROG_SCENES,
  AUCTION_WHEEL_FROG_EVENT_SCENES,
  pickAuctionWheelFrogImage,
  pickNextAuctionWheelFrogScene,
} from './auctionWheelFrogScenes';

const INITIAL_DELAY = { min: 3500, max: 6500 };
const SCENE_GAP = { min: 9000, max: 16000 };

const randomDelay = ({ min, max }) =>
  Math.round(min + Math.random() * (max - min));

const getReducedMotionPreference = () =>
  typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const resolveEventScene = ({ isSpinning, spinDuration, showElimination, showWinner }) => {
  if (showWinner) {
    return AUCTION_WHEEL_FROG_EVENT_SCENES.winner;
  }
  if (showElimination) {
    return AUCTION_WHEEL_FROG_EVENT_SCENES.elimination;
  }
  if (isSpinning && Number(spinDuration) >= 10) {
    return AUCTION_WHEEL_FROG_EVENT_SCENES.spinning;
  }
  return null;
};

export const useAuctionWheelFrogScene = ({
  isSpinning,
  spinDuration,
  showElimination,
  showWinner,
}) => {
  const [activeScene, setActiveScene] = useState(null);
  const [reducedMotion, setReducedMotion] = useState(getReducedMotionPreference);
  const previousAmbientSceneId = useRef(null);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = (event) => setReducedMotion(event.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    let timeoutId;
    let cancelled = false;
    const eventScene = resolveEventScene({
      isSpinning,
      spinDuration,
      showElimination,
      showWinner,
    });

    if (reducedMotion) {
      setActiveScene(null);
      return undefined;
    }

    if (eventScene) {
      setActiveScene(pickAuctionWheelFrogImage(eventScene));
      timeoutId = window.setTimeout(() => {
        if (!cancelled) {
          setActiveScene(null);
        }
      }, eventScene.duration);

      return () => {
        cancelled = true;
        window.clearTimeout(timeoutId);
      };
    }

    if (isSpinning) {
      setActiveScene(null);
      return undefined;
    }

    const scheduleAmbientScene = (initial = false) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        if (cancelled || document.visibilityState === 'hidden') {
          return;
        }

        const scene = pickNextAuctionWheelFrogScene(
          AMBIENT_AUCTION_WHEEL_FROG_SCENES,
          previousAmbientSceneId.current,
        );
        if (!scene) {
          scheduleAmbientScene(false);
          return;
        }

        previousAmbientSceneId.current = scene.id;
        setActiveScene(pickAuctionWheelFrogImage(scene));
        timeoutId = window.setTimeout(() => {
          setActiveScene(null);
          scheduleAmbientScene(false);
        }, scene.duration);
      }, randomDelay(initial ? INITIAL_DELAY : SCENE_GAP));
    };

    const handleVisibilityChange = () => {
      window.clearTimeout(timeoutId);
      setActiveScene(null);
      if (document.visibilityState === 'visible') {
        scheduleAmbientScene(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    scheduleAmbientScene(true);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSpinning, reducedMotion, showElimination, showWinner, spinDuration]);

  return reducedMotion ? null : activeScene;
};
